// ═══════════════════════════════════════════════════════════════════════════════
// Share pairing invite QR as PNG (mobile Web Share API)
// ═══════════════════════════════════════════════════════════════════════════════

import { LANGUAGE, type Language } from '@fdh/shared';
import { formatMessage, UI_MESSAGES } from '../i18n/uiMessages';

const SHARE_PNG_SIZE = 512;

const messageForLanguage = (language: Language, key: string): string =>
  UI_MESSAGES[language][key] ?? UI_MESSAGES[LANGUAGE.EN][key] ?? key;

const resolveShareNickname = (language: Language, nickname: string): string => {
  const trimmed = nickname.trim();
  if (trimmed) {
    return trimmed;
  }
  return (
    formatMessage(messageForLanguage(language, 'pairing.generate.shareEmployerFallback')) ||
    'Employer'
  );
};

const formatShareBody = (language: Language, nickname: string): string =>
  formatMessage(messageForLanguage(language, 'pairing.generate.shareInviteText'), {
    nickname: resolveShareNickname(language, nickname),
  });

/**
 * Non-English UI: employer language + English + link once.
 * English UI: English only + link once.
 */
export const buildPairingInviteShareText = (options: {
  language: Language;
  nickname: string;
  link: string;
}): string => {
  const englishBody = formatShareBody(LANGUAGE.EN, options.nickname);
  if (options.language === LANGUAGE.EN) {
    return `${englishBody}\n${options.link}`;
  }
  const localBody = formatShareBody(options.language, options.nickname);
  return `${localBody}\n\n${englishBody}\n${options.link}`;
};

const isMobileLikeDevice = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
  const narrowViewport = window.matchMedia('(max-width: 768px)').matches;
  const hasTouch = navigator.maxTouchPoints > 0;
  return coarsePointer || (hasTouch && narrowViewport);
};

const canShareImageFiles = (): boolean => {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return false;
  }
  try {
    const probe = new File([new Blob(['x'], { type: 'image/png' })], 'probe.png', {
      type: 'image/png',
    });
    if (typeof navigator.canShare === 'function') {
      return navigator.canShare({ files: [probe] });
    }
    return true;
  } catch {
    return false;
  }
};

/** Mobile Safari / Chrome (incl. PWA, Android TWA) with file share; hidden on desktop. */
export const canShowPairingInviteShareButton = (): boolean =>
  isMobileLikeDevice() && canShareImageFiles();

const loadImageFromUrl = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load QR image'));
    img.src = url;
  });

export const svgElementToPngFile = async (
  svgElement: SVGElement,
  filename: string
): Promise<File> => {
  const serializer = new XMLSerializer();
  let svgString = serializer.serializeToString(svgElement);
  if (!svgString.includes('xmlns=')) {
    svgString = svgString.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
  }
  if (!/\swidth=/.test(svgString)) {
    svgString = svgString.replace('<svg', `<svg width="${SHARE_PNG_SIZE}"`);
  }
  if (!/\sheight=/.test(svgString)) {
    svgString = svgString.replace('<svg', `<svg height="${SHARE_PNG_SIZE}"`);
  }

  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
  const img = await loadImageFromUrl(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = SHARE_PNG_SIZE;
  canvas.height = SHARE_PNG_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas not available');
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, SHARE_PNG_SIZE, SHARE_PNG_SIZE);
  const padding = 24;
  const drawSize = SHARE_PNG_SIZE - padding * 2;
  ctx.drawImage(img, padding, padding, drawSize, drawSize);

  const pngBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to encode PNG'));
        return;
      }
      resolve(blob);
    }, 'image/png');
  });

  return new File([pngBlob], filename, { type: 'image/png' });
};

export const sharePairingInviteQr = async (options: {
  svgElement: SVGElement;
  title: string;
  text: string;
}): Promise<'shared' | 'cancelled'> => {
  const file = await svgElementToPngFile(options.svgElement, 'maideasy-invite-qr.png');
  // Do not set `url` — invite link is already in `text`; apps like WhatsApp would duplicate it.
  const payload: ShareData = {
    title: options.title,
    text: options.text,
    files: [file],
  };

  if (typeof navigator.canShare === 'function' && !navigator.canShare(payload)) {
    await navigator.share({
      title: options.title,
      text: options.text,
    });
    return 'shared';
  }

  await navigator.share(payload);
  return 'shared';
};

export const isShareCancelledError = (err: unknown): boolean => {
  if (!(err instanceof DOMException)) {
    return false;
  }
  return err.name === 'AbortError';
};
